/**
 * Image Optimization Utility
 * Provides functions for optimizing image loading across the application.
 */

const IMAGE_CACHE = new Map(); // Cache for optimized images
const API_BASE_URL = process.env.VUE_APP_BASE_URL || '';
const DEFAULT_PLACEHOLDER = '/assets/placeholder.jpg'; // Path to default placeholder
const CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours cache validity

/**
 * Normalize image URL to absolute path
 * @param {string} url - Raw image URL
 * @return {string} - Normalized absolute URL
 */
export function normalizeImageUrl(url) {
  if (!url) return DEFAULT_PLACEHOLDER;

  // Already an absolute URL
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // Remove leading slash if present
  const normalizedPath = url.startsWith('/') ? url.substring(1) : url;

  // Remove trailing slash from base URL
  const baseUrl = API_BASE_URL.replace(/\/+$/, '');

  // Combine to create absolute URL
  return `${baseUrl}/${normalizedPath}`;
}

/**
 * Pre-process image and store in cache
 * @param {string} url - Image URL
 * @param {Object} options - Pre-processing options
 * @return {Object} - Processed image data
 */
export function preprocessImage(url, options = {}) {
  if (!url) return { url: DEFAULT_PLACEHOLDER, status: 'error' };

  const cacheKey = `${url}-${JSON.stringify(options)}`;

  // Return from cache if available
  if (IMAGE_CACHE.has(cacheKey)) {
    const cached = IMAGE_CACHE.get(cacheKey);
    if (Date.now() - cached.timestamp < CACHE_EXPIRY) {
      return cached;
    }
  }

  // Process image based on options
  const normalizedUrl = normalizeImageUrl(url);
  const result = {
    originalUrl: url,
    url: normalizedUrl,
    timestamp: Date.now(),
    width: options.width,
    height: options.height,
    status: 'processed',
  };

  // Apply responsive sizing if requested
  if (options.responsive) {
    result.thumbnailUrl = `${normalizedUrl}?width=${options.width || 300}&height=${options.height || 200}&fit=cover`;
    result.smallUrl = `${normalizedUrl}?width=${options.width ? Math.floor(options.width * 0.5) : 150}&height=${options.height ? Math.floor(options.height * 0.5) : 100}&fit=cover`;
  }

  // Store in cache
  IMAGE_CACHE.set(cacheKey, result);
  return result;
}

/**
 * Preload images to improve perceived performance
 * @param {Array<string>} urls - List of image URLs
 */
export function preloadImages(urls) {
  urls.forEach((url) => {
    const img = new Image();
    img.src = normalizeImageUrl(url);
  });
}

export default {
  normalizeImageUrl,
  preprocessImage,
  preloadImages,
};